/** The paint-bucket: turn a click inside an enclosed area into a VECTOR fill.
 *
 * A raster flood fill is the only honest way to answer "what did the user mean
 * by this area" — the boundary is whatever ink happens to be there, which no
 * amount of geometry over the stroke list can reconstruct. So we rasterize the
 * markup, flood it like MS Paint, and then trace the result back into polygons,
 * so what gets STORED is still page-relative [0,1] geometry that scales with
 * zoom and prints as vectors. Nothing raster is ever persisted.
 *
 * The pipeline, and why each step exists:
 *
 *   1. `barrierFromPixels` — anything drawn is a wall. The paper (ruled lines,
 *      grid, colour) is CSS behind the canvas, not pixels in it, so a fill
 *      spreads across ruled lines exactly as it should.
 *   2. `dilate(barrier, gap)` — "tiny gaps in the boundary are artifacts". A
 *      hand-drawn box rarely closes exactly; at speed a stroke can leave a
 *      1–3px hole. Thickening the walls seals those before flooding, so the
 *      paint doesn't escape through a gap the user cannot even see.
 *   3. `floodRegion` — 4-connected scanline flood from the seed.
 *   4. `growWithin(region, ORIGINAL barrier, gap)` — step 2 also pushed the
 *      region back from every real edge by `gap`, which would leave a halo of
 *      unfilled paper. Grow it back, but only into pixels that were never
 *      barriers, so the fill runs up to the ink and stops.
 *   5. `traceRings` — crack-following around the region: outer boundary AND the
 *      boundary of anything enclosed, so a fill around a shape leaves that shape
 *      unfilled (rendered even-odd).
 *   6. `simplifyRing` — Douglas-Peucker, because a traced ring is one point per
 *      pixel edge and nobody needs 40,000 of them.
 *
 * Everything here is pure and works on plain arrays, so the whole thing is
 * unit-testable without a canvas.
 */

export interface FillPoint {
  x: number;
  y: number;
}

/** A pixel is a wall if the rendered markup is more than faintly opaque there.
 * Anti-aliased stroke edges fade to nothing, so a low threshold keeps a
 * hairline's soft edge counting as a wall rather than a leak. */
export function barrierFromPixels(
  rgba: Uint8ClampedArray,
  w: number,
  h: number,
  alphaThreshold = 24,
): Uint8Array {
  const out = new Uint8Array(w * h);
  for (let i = 0, p = 3; i < out.length; i++, p += 4) {
    if (rgba[p]! >= alphaThreshold) out[i] = 1;
  }
  return out;
}

/** Grow every set pixel by `r` in each direction (a square structuring element,
 * applied separably — two O(n·r) passes rather than one O(n·r²)). */
export function dilate(src: Uint8Array, w: number, h: number, r: number): Uint8Array {
  if (r <= 0) return src.slice();
  const mid = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    const row = y * w;
    for (let x = 0; x < w; x++) {
      if (!src[row + x]) continue;
      const from = Math.max(0, x - r);
      const to = Math.min(w - 1, x + r);
      for (let k = from; k <= to; k++) mid[row + k] = 1;
    }
  }
  const out = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    const row = y * w;
    for (let x = 0; x < w; x++) {
      if (!mid[row + x]) continue;
      const from = Math.max(0, y - r);
      const to = Math.min(h - 1, y + r);
      for (let k = from; k <= to; k++) out[k * w + x] = 1;
    }
  }
  return out;
}

export interface FloodResult {
  region: Uint8Array;
  count: number;
  /** The flood reached the edge of the raster — the area is NOT enclosed, and
   * filling it would flood the whole page. */
  touchedEdge: boolean;
}

/** 4-connected scanline flood from (sx, sy) over everything that is not a
 * barrier. Returns null when the seed itself is a wall (the user clicked ON a
 * stroke, which fills nothing). */
export function floodRegion(
  barrier: Uint8Array,
  w: number,
  h: number,
  sx: number,
  sy: number,
): FloodResult | null {
  if (sx < 0 || sy < 0 || sx >= w || sy >= h) return null;
  if (barrier[sy * w + sx]) return null;
  const region = new Uint8Array(w * h);
  const stack: number[] = [sx, sy];
  let count = 0;
  let touchedEdge = false;
  while (stack.length) {
    const y = stack.pop()!;
    let x = stack.pop()!;
    const row = y * w;
    if (region[row + x] || barrier[row + x]) continue;
    // Walk left and right to the ends of this span.
    let left = x;
    while (left > 0 && !barrier[row + left - 1] && !region[row + left - 1]) left--;
    let right = x;
    while (right < w - 1 && !barrier[row + right + 1] && !region[row + right + 1]) right++;
    if (left === 0 || right === w - 1) touchedEdge = true;
    if (y === 0 || y === h - 1) touchedEdge = true;
    for (x = left; x <= right; x++) {
      region[row + x] = 1;
      count++;
      // Seed the rows above and below wherever they are open.
      if (y > 0) {
        const up = row - w + x;
        if (!barrier[up] && !region[up]) stack.push(x, y - 1);
      }
      if (y < h - 1) {
        const down = row + w + x;
        if (!barrier[down] && !region[down]) stack.push(x, y + 1);
      }
    }
  }
  return { region, count, touchedEdge };
}

/** Grow `region` by `r` INTO barrier pixels only — never into open space.
 *
 * A stroke's edges are anti-aliased, so its outermost pixels are barely-there
 * greys that still count as walls. Stopping at them leaves a pale halo between
 * the paint and the line, and at a corner — where two soft edges overlap and the
 * wall is at its thickest — a visible notch. Tucking the fill a couple of pixels
 * UNDER the ink removes both, and the fill is drawn behind the strokes anyway.
 *
 * Growth is confined to pixels that were already walls, so it can reach the far
 * side of a line but can never spill into the open area beyond it. */
export function growIntoBarrier(
  region: Uint8Array,
  barrier: Uint8Array,
  w: number,
  h: number,
  r: number,
): Uint8Array {
  if (r <= 0) return region.slice();
  let cur = region;
  for (let step = 0; step < r; step++) {
    const next = cur.slice();
    for (let y = 0; y < h; y++) {
      const row = y * w;
      for (let x = 0; x < w; x++) {
        const i = row + x;
        if (cur[i] || !barrier[i]) continue; // only ever into the ink itself
        const x0 = Math.max(0, x - 1);
        const x1 = Math.min(w - 1, x + 1);
        const y0 = Math.max(0, y - 1);
        const y1 = Math.min(h - 1, y + 1);
        let touching = false;
        for (let ny = y0; ny <= y1 && !touching; ny++) {
          for (let nx = x0; nx <= x1; nx++) {
            if (cur[ny * w + nx]) {
              touching = true;
              break;
            }
          }
        }
        if (touching) next[i] = 1;
      }
    }
    cur = next;
  }
  return cur;
}

/** Grow `region` by `r`, but never into `barrier`. Undoes the inset that
 * gap-closing dilation introduced, so the fill meets the real ink. */
export function growWithin(
  region: Uint8Array,
  barrier: Uint8Array,
  w: number,
  h: number,
  r: number,
): Uint8Array {
  if (r <= 0) return region.slice();
  let cur = region;
  for (let step = 0; step < r; step++) {
    const next = cur.slice();
    for (let y = 0; y < h; y++) {
      const row = y * w;
      for (let x = 0; x < w; x++) {
        const i = row + x;
        if (cur[i] || barrier[i]) continue;
        // 8-connected, to match `dilate`'s SQUARE structuring element. Growing
        // back 4-connected would under-recover the corners the square dilation
        // ate, leaving a small notch of bare paper inside every corner.
        const x0 = Math.max(0, x - 1);
        const x1 = Math.min(w - 1, x + 1);
        const y0 = Math.max(0, y - 1);
        const y1 = Math.min(h - 1, y + 1);
        let touching = false;
        for (let ny = y0; ny <= y1 && !touching; ny++) {
          for (let nx = x0; nx <= x1; nx++) {
            if (cur[ny * w + nx]) {
              touching = true;
              break;
            }
          }
        }
        if (touching) next[i] = 1;
      }
    }
    cur = next;
  }
  return cur;
}

/** Trace every closed boundary of `region` by following the "cracks" between a
 * set pixel and an unset neighbour. Each set pixel contributes up to four unit
 * edges, oriented so they link head-to-tail; the loops that fall out are the
 * outer boundary plus one ring per enclosed hole. Coordinates are pixel-corner
 * lattice points, so a single pixel traces as a 1×1 square.
 *
 * Winding is deliberately not normalized: the renderers fill even-odd, where a
 * hole is a hole whichever way round it goes. */
export function traceRings(region: Uint8Array, w: number, h: number): FillPoint[][] {
  // Directed unit edges keyed by their start lattice point, so a ring is walked
  // by repeatedly asking "which edge leaves the point I just arrived at".
  const edges = new Map<number, number[]>();
  const key = (x: number, y: number) => y * (w + 1) + x;
  const add = (x0: number, y0: number, x1: number, y1: number) => {
    const k = key(x0, y0);
    const list = edges.get(k);
    if (list) list.push(key(x1, y1));
    else edges.set(k, [key(x1, y1)]);
  };
  const on = (x: number, y: number) => x >= 0 && y >= 0 && x < w && y < h && !!region[y * w + x];

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (!region[y * w + x]) continue;
      if (!on(x, y - 1)) add(x, y, x + 1, y); // top edge, rightwards
      if (!on(x + 1, y)) add(x + 1, y, x + 1, y + 1); // right edge, downwards
      if (!on(x, y + 1)) add(x + 1, y + 1, x, y + 1); // bottom edge, leftwards
      if (!on(x - 1, y)) add(x, y + 1, x, y); // left edge, upwards
    }
  }

  const rings: FillPoint[][] = [];
  for (const [start] of edges) {
    // `edges` is consumed as it is walked, so entries emptied by an earlier ring
    // are skipped here.
    while ((edges.get(start)?.length ?? 0) > 0) {
      const ring: FillPoint[] = [];
      let at = start;
      while (true) {
        const outs = edges.get(at);
        if (!outs || !outs.length) break;
        const next = outs.pop()!;
        ring.push({ x: at % (w + 1), y: Math.floor(at / (w + 1)) });
        at = next;
        if (at === start) break;
      }
      if (ring.length >= 4) rings.push(ring);
    }
  }
  return rings;
}

/** Perpendicular distance from p to the segment a→b. */
function pointSegDist(a: FillPoint, b: FillPoint, p: FillPoint): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const l2 = dx * dx + dy * dy;
  if (l2 === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/** Douglas-Peucker on an OPEN polyline. */
function simplifyOpen(pts: FillPoint[], eps: number): FillPoint[] {
  if (pts.length < 3) return pts.slice();
  let worst = 0;
  let idx = 0;
  const a = pts[0]!;
  const b = pts[pts.length - 1]!;
  for (let i = 1; i < pts.length - 1; i++) {
    const d = pointSegDist(a, b, pts[i]!);
    if (d > worst) {
      worst = d;
      idx = i;
    }
  }
  if (worst <= eps) return [a, b];
  const left = simplifyOpen(pts.slice(0, idx + 1), eps);
  const right = simplifyOpen(pts.slice(idx), eps);
  return [...left.slice(0, -1), ...right];
}

/** Douglas-Peucker for a CLOSED ring. A traced ring is one point per pixel
 * edge — thousands of collinear steps — and none of that detail survives being
 * drawn. Split at the two most distant points so the simplification isn't
 * anchored to an arbitrary start vertex. */
export function simplifyRing(ring: FillPoint[], eps: number): FillPoint[] {
  if (ring.length < 4) return ring.slice();
  const a = ring[0]!;
  let far = 1;
  let best = -1;
  for (let i = 1; i < ring.length; i++) {
    const d = Math.hypot(ring[i]!.x - a.x, ring[i]!.y - a.y);
    if (d > best) {
      best = d;
      far = i;
    }
  }
  const first = simplifyOpen(ring.slice(0, far + 1), eps);
  const second = simplifyOpen([...ring.slice(far), a], eps);
  const out = [...first.slice(0, -1), ...second.slice(0, -1)];
  return out.length >= 3 ? out : ring.slice();
}

export interface FillOptions {
  /** How wide a boundary gap still counts as closed, in mask pixels. */
  gap?: number;
  /** Douglas-Peucker tolerance, in mask pixels. */
  epsilon?: number;
  /** Ignore a traced ring enclosing fewer than this many pixels — speckle from
   * anti-aliasing, not something anyone meant to leave unfilled. */
  minRingArea?: number;
  /** Refuse a fill smaller than this, in mask pixels: a click into a hairline
   * crack between two strokes should do nothing rather than persist a sliver. */
  minArea?: number;
  /** How far the finished fill tucks UNDER the ink that bounds it, in mask
   * pixels. Removes the anti-aliasing halo and the notch at every corner. */
  underlap?: number;
}

export type FillFailure = "on-stroke" | "unbounded" | "too-small" | "no-shape";

export type FillOutcome =
  | { ok: true; rings: FillPoint[][]; pixels: number }
  | { ok: false; reason: FillFailure };

/** The whole pipeline: rendered markup + a seed → polygon rings in MASK pixel
 * coordinates (the caller maps them into page-relative space). */
export function computeFill(
  rgba: Uint8ClampedArray,
  w: number,
  h: number,
  seed: FillPoint,
  opts: FillOptions = {},
): FillOutcome {
  const gap = opts.gap ?? 3;
  // Tight enough that a right angle stays a right angle. A traced ring is one
  // point per pixel edge, so the simplification is what decides whether corners
  // survive; at 1.2px they visibly rounded off.
  const epsilon = opts.epsilon ?? 0.6;
  const minArea = opts.minArea ?? 24;
  const minRingArea = opts.minRingArea ?? 12;
  const underlap = opts.underlap ?? 2;

  const barrier = barrierFromPixels(rgba, w, h);
  const sx = Math.round(seed.x);
  const sy = Math.round(seed.y);
  if (sx < 0 || sy < 0 || sx >= w || sy >= h) return { ok: false, reason: "on-stroke" };
  if (barrier[sy * w + sx]) return { ok: false, reason: "on-stroke" };

  // Seal the walls by `gap`, then BACK OFF until the area survives it. Thick
  // walls close the hand-drawn gaps, but a narrow area is only a few pixels
  // across — at full gap its walls meet in the middle and swallow it whole, and
  // the honest answer to "I sealed away the thing you clicked" is to seal less,
  // not to fill nothing (or to fall straight back to leaky walls and flood the
  // page). Whichever radius first yields an ENCLOSED region wins.
  let used = -1;
  let flood: FloodResult | null = null;
  for (let g = gap; g >= 0; g--) {
    const candidate = floodRegion(g > 0 ? dilate(barrier, w, h, g) : barrier, w, h, sx, sy);
    if (!candidate || candidate.touchedEdge) continue;
    flood = candidate;
    used = g;
    break;
  }
  if (!flood) return { ok: false, reason: "unbounded" };

  // Up to the ink, then a little way under it (see growIntoBarrier).
  const grown = growIntoBarrier(
    growWithin(flood.region, barrier, w, h, used),
    barrier,
    w,
    h,
    underlap,
  );
  let pixels = 0;
  for (let i = 0; i < grown.length; i++) if (grown[i]) pixels++;
  if (pixels < minArea) return { ok: false, reason: "too-small" };

  // The BIGGEST ring is the fill itself and is always kept — `minRingArea` only
  // culls speckle HOLES (a stray anti-aliased pixel inside the area is not
  // something anyone meant to leave unpainted). Applying it to every ring would
  // throw away a small fill entirely.
  const traced = traceRings(grown, w, h);
  let biggest = 0;
  traced.forEach((r, i) => {
    if (Math.abs(ringArea(r)) > Math.abs(ringArea(traced[biggest]!))) biggest = i;
  });
  const rings = traced
    .filter((r, i) => i === biggest || Math.abs(ringArea(r)) >= minRingArea)
    .map((r) => simplifyRing(r, epsilon))
    .filter((r) => r.length >= 3);
  if (!rings.length) return { ok: false, reason: "no-shape" };
  return { ok: true, rings, pixels };
}

/** Signed area of a ring (shoelace) — used only for magnitude, to drop speckle. */
export function ringArea(ring: FillPoint[]): number {
  let sum = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]!;
    const b = ring[(i + 1) % ring.length]!;
    sum += a.x * b.y - b.x * a.y;
  }
  return sum / 2;
}
